import { MyMediaEntryResultType } from "@kaltura/mediaspace-shared-types";
import {
    decodeMediaCollaborationType,
    formattedNumber,
    translate,
    translateTime,
    useMediaQuery,
} from "@kaltura/mediaspace-shared-utils";
import { StyledMediaInfo, StyledSeparator } from "./styled-components";
import PublishIndicator from "@kaltura/mediaspace-shared-entry/publish/publish-indicator/PublishIndicator";
import { TruncateWithTooltip, CustomComponent } from "@kaltura/mediaspace-shared-ui";
import { Typography } from "@kaltura/ds-react-components";
import styled from "@emotion/styled";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { useContext } from "react";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";

const StyledCollaborationTypography = styled(Typography)({
    display: "block",
});

const NoShrinkSpan = styled("span")({
    flexShrink: 0,
});

export interface MediaInfoProps {
    media: MyMediaEntryResultType;
    showFullData: boolean;
}

const MediaInfo = (props: MediaInfoProps) => {
    const {
        collaborationInfo = [],
        object: entry,
        publishData,
        plays,
        views,
        mediaTypeName,
        lastWatched,
        moreInfo,
    } = props.media;

    const theme = useTheme();
    const isMdScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.lg));
    const isSmScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));
    const mdOnly = isMdScreen && !isSmScreen;

    const configContext = useContext(ConfigContext);
    const { dateFormats } = configContext.application;

    const collaboration = decodeMediaCollaborationType(collaborationInfo.join(","));

    // translatePlural can't be used here since formattedNumber returns a string, not a number
    let viewsString = "";
    if (views !== undefined) {
        viewsString = views === 1 ? translate("%1 View", [1]) : translate("%1 Views", [formattedNumber(views)]);
    }

    let playsString = "";
    if (plays !== undefined) {
        playsString = plays === 1 ? translate("%1 Play", [1]) : translate("%1 Plays", [formattedNumber(plays)]);
    }

    // show publish data if this is widelayout or if there is no views data
    const showPublishData = publishData && (!mdOnly || props.showFullData || (!viewsString && !playsString));

    let formattedWatchedAt = null;
    if (lastWatched) {
        const watchedAtFormat = dateFormats.shortDate;
        formattedWatchedAt = translateTime(lastWatched, watchedAtFormat);
    }

    return (
        <StyledMediaInfo className={"kms-ds-media-info"}>
            {mediaTypeName}

            {collaboration && (
                <>
                    <StyledSeparator />
                    <TruncateWithTooltip text={collaboration}>
                        <StyledCollaborationTypography variant="body2Highlight">
                            {collaboration}
                        </StyledCollaborationTypography>
                    </TruncateWithTooltip>
                </>
            )}
            {!!viewsString && (
                <>
                    <StyledSeparator />
                    <NoShrinkSpan>{viewsString}</NoShrinkSpan>
                </>
            )}
            {!!playsString && (
                <>
                    <StyledSeparator />
                    <NoShrinkSpan>{playsString}</NoShrinkSpan>
                </>
            )}
            {showPublishData && (
                <>
                    <StyledSeparator />
                    <NoShrinkSpan>
                        <PublishIndicator publishData={publishData} entryId={entry.id} />
                    </NoShrinkSpan>
                </>
            )}
            {formattedWatchedAt && (
                <>
                    <StyledSeparator />
                    <NoShrinkSpan>{translate("Watched %1", [formattedWatchedAt])}</NoShrinkSpan>
                </>
            )}
            {moreInfo && moreInfo.length > 0 && (
                <>
                    {moreInfo.map((moreInfoComponent, index) => (
                        <>
                            <StyledSeparator />
                            <CustomComponent key={`media-info-more-info-${index}`} component={moreInfoComponent} />
                        </>
                    ))}
                </>
            )}
        </StyledMediaInfo>
    );
};

export default MediaInfo;
